import { useListItem, useMergeRefs } from '@floating-ui/react';
import { forwardRef, type Ref } from 'react';

import type {
  MenuButtonItemProps,
  MenuElement,
  MenuLinkItemProps,
} from '../types';

import { useLinkElement } from '../../../context/LinkNavigation/LinkNavigation';
import { Icon } from '../../Icon/Icon';
import { Layout } from '../../Layout/Layout';
import { Text } from '../../Text/Text';
import { Tooltip } from '../../Tooltip/Tooltip';
import './MenuItem.css';

/* Rows are full-width siblings, so a top/bottom tooltip covers the adjacent
   item. Anchoring every row to the same side keeps `flip` choosing between
   left and right — never the cross axis — so placement stays predictable
   however much room the surface has above or below it. */
const TOOLTIP_DIRECTION = 'right';
const TOOLTIP_PRIVATE = { lockPlacementAxis: true };

export const MenuButtonItem = forwardRef<MenuElement, MenuButtonItemProps>(
  (
    {
      disabled,
      download,
      href,
      icon,
      label,
      rel,
      target,
      text,
      tooltip,
      onClick,
      __private,
      ...props
    },
    forwardedRef,
  ) => {
    const { getItemProps } = __private;
    const item = useListItem({ label: disabled ? null : label });
    const isSoftDisabled = disabled && Boolean(tooltip);
    const iconPosition = icon?.position ?? 'left';
    // Drop the href while disabled so the link can't be followed.
    const resolvedHref = href && !disabled ? href : undefined;
    const LinkElement = useLinkElement(resolvedHref, target);
    const mergedRef = useMergeRefs([item.ref, forwardedRef]);

    const itemLabel = (
      <Layout align="center" justify="between">
        <Layout align="center" spacing="s">
          {icon && iconPosition === 'left' && (
            <Icon filled={icon.filled} icon={icon.key} size="s" />
          )}
          <Text>{text}</Text>
        </Layout>
        {icon && iconPosition === 'right' && (
          <Icon filled={icon.filled} icon={icon.key} size="s" />
        )}
      </Layout>
    );

    // Two independent `return`s rather than a ternary: the inspector's build
    // stamper resolves a conditional return to a single branch, so a shared
    // ternary would only ever stamp one of the two element types.
    if (resolvedHref) {
      return (
        <Tooltip
          direction={TOOLTIP_DIRECTION}
          tooltip={tooltip?.text}
          __private={TOOLTIP_PRIVATE}
        >
          <LinkElement
            {...getItemProps({
              ...props,
              onClick: isSoftDisabled ? undefined : onClick,
            })}
            aria-disabled={isSoftDisabled ? true : undefined}
            aria-label={label}
            className="eds-menu--menu-item"
            download={download}
            href={resolvedHref}
            rel={
              rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)
            }
            ref={mergedRef as Ref<HTMLAnchorElement>}
            role="menuitem"
            target={target}
          >
            {itemLabel}
          </LinkElement>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        direction={TOOLTIP_DIRECTION}
        tooltip={tooltip?.text}
        __private={TOOLTIP_PRIVATE}
      >
        <button
          {...getItemProps({
            ...props,
            onClick: isSoftDisabled ? undefined : onClick,
          })}
          aria-disabled={isSoftDisabled ? true : undefined}
          aria-label={label}
          className="eds-menu--menu-item"
          disabled={isSoftDisabled ? undefined : disabled}
          ref={mergedRef as Ref<HTMLButtonElement>}
          role="menuitem"
          type="button"
        >
          {itemLabel}
        </button>
      </Tooltip>
    );
  },
);

MenuButtonItem.displayName = 'MenuButtonItem';

export const MenuLinkItem = forwardRef<HTMLAnchorElement, MenuLinkItemProps>(
  ({ path, text, onClick, __private, ...props }, forwardedRef) => {
    const { getItemProps } = __private;
    const item = useListItem({ label: text });
    const LinkElement = useLinkElement(path);

    return (
      <LinkElement
        {...getItemProps({
          ...props,
          onClick,
        })}
        aria-label={text}
        className="eds-menu--menu-item"
        href={path}
        ref={useMergeRefs([item.ref, forwardedRef])}
        role="menuitem"
      >
        <Text>{text}</Text>
      </LinkElement>
    );
  },
);

MenuLinkItem.displayName = 'MenuLinkItem';
